import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Tooltip,
  Avatar,
  Collapse,
  ListItemIcon,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CalculateIcon from '@mui/icons-material/Calculate';
import HomeIcon from '@mui/icons-material/Home';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RuleIcon from '@mui/icons-material/Rule';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ScienceIcon from '@mui/icons-material/Science';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ThumbsUpDownIcon from '@mui/icons-material/ThumbsUpDown';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { useKeycloak } from '@react-keycloak/web';
import { useTranslation } from 'react-i18next';
import { NewsIcon } from '../News';
import { useInstallPWA } from '../../contexts/PWAContext';

function Layout({ children }) {
  const { t } = useTranslation();
  
  const pages = [
    { name: t('nav.home'), path: '/home', icon: <HomeIcon />, public: true },
    { name: t('nav.pricing'), path: '/pricing', icon: <DescriptionIcon />, unauthOnly: true },
    { name: t('nav.about'), path: '/help/about', icon: <HelpOutlineIcon />, unauthOnly: true },
    { name: t('nav.graph'), path: '/graph', icon: <ShowChartIcon />, protected: true },
    { name: t('nav.foodList'), path: '/food-list', icon: <RestaurantMenuIcon />, protected: true, role: 'feature-food' },
    { 
      name: t('nav.calculators'), 
      icon: <CalculateIcon />, 
      protected: true,
      submenu: [
        { name: t('nav.iob'), path: '/calc/iob', icon: <CalculateIcon /> },
        { name: t('nav.dosingRules'), path: '/calc/rules', icon: <RuleIcon /> },
      ]
    },
    {
      name: t('nav.log'),
      icon: <AssignmentIcon />,
      protected: true,
      submenu: [
        { name: t('nav.glucose'), path: '/log/glucose', icon: <WaterDropIcon /> },
        { name: t('nav.ketones'), path: '/log/ketones', icon: <ScienceIcon /> },
        { name: t('nav.hba1c'), path: '/log/hba1c', icon: <BloodtypeIcon /> },
        { name: t('nav.sensors'), path: '/sensors', icon: <RadioButtonCheckedIcon /> },
      ]
    },
    { name: t('nav.profiles'), path: '/profiles', icon: <VaccinesIcon />, protected: true },
    { name: t('nav.medicalReport'), path: '/reports/medical', icon: <AssessmentIcon />, protected: true },
    {
      name: t('nav.admin'),
      icon: <AdminPanelSettingsIcon />,
      protected: true,
      adminMenu: true,
      submenu: [
        { name: t('nav.dashboard'), path: '/admin/dashboard', icon: <DashboardIcon />, role: 'dashboard-admin' },
        { name: t('nav.users'), path: '/admin/users', icon: <PeopleIcon />, role: 'users-admin' },
        { name: t('nav.statistics'), path: '/admin/statistics', icon: <BarChartIcon />, role: 'statistics-admin' },
        { name: t('nav.foodAdmin'), path: '/admin/food', icon: <RestaurantMenuIcon />, role: 'food-admin' },
        { name: t('nav.supportedCountries'), path: '/admin/countries', icon: <PublicIcon />, role: 'dashboard-admin' },
        { name: t('nav.pricing'), path: '/admin/pricing', icon: <AttachMoneyIcon />, role: 'price-admin' },
        { name: t('nav.reclaim'), path: '/admin/reclaim', icon: <AutorenewIcon />, role: 'sensor-reclaim-admin' },
        { name: t('nav.aiPrompts'), path: '/admin/ai-prompts', icon: <SmartToyIcon />, role: 'food-admin' },
        { name: t('nav.mealVotes'), path: '/admin/meal-votes', icon: <ThumbsUpDownIcon />, role: 'food-admin' },
      ]
    },
  ];
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElCalculators, setAnchorElCalculators] = useState(null);
  const [anchorElLog, setAnchorElLog] = useState(null);
  const [anchorElAdmin, setAnchorElAdmin] = useState(null);
  const [adminOpenMobile, setAdminOpenMobile] = useState(false);
  const [showIOSDialog, setShowIOSDialog] = useState(false);

  // PWA install hook
  const { isIOS, isStandalone, canInstall, handleInstall } = useInstallPWA();

  // Refs for hover timeout handling
  const calculatorsTimeoutRef = useRef(null);
  const logTimeoutRef = useRef(null);
  const adminTimeoutRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { keycloak, initialized } = useKeycloak();
  
  const isAuthenticated = keycloak?.authenticated || false;
  const user = keycloak?.tokenParsed;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleUserMenuClick = (action) => {
    handleCloseUserMenu();
    if (action === 'settings') {
      navigate('/settings');
    } else if (action === 'logout') {
      keycloak.logout();
    }
  };

  const handleCalculatorsClick = (event) => {
    setAnchorElCalculators(event.currentTarget);
  };

  const handleCalculatorsClose = () => {
    setAnchorElCalculators(null);
  };

  const handleCalculatorsMouseEnter = (event) => {
    if (calculatorsTimeoutRef.current) {
      clearTimeout(calculatorsTimeoutRef.current);
      calculatorsTimeoutRef.current = null;
    }
    setAnchorElCalculators(event.currentTarget);
  };

  const handleCalculatorsMouseLeave = () => {
    calculatorsTimeoutRef.current = setTimeout(() => {
      setAnchorElCalculators(null);
    }, 150);
  };

  const handleCalculatorsMenuEnter = () => {
    if (calculatorsTimeoutRef.current) {
      clearTimeout(calculatorsTimeoutRef.current);
      calculatorsTimeoutRef.current = null;
    }
  };

  const handleLogClick = (event) => {
    setAnchorElLog(event.currentTarget);
  };

  const handleLogClose = () => {
    setAnchorElLog(null);
  };

  const handleLogMouseEnter = (event) => {
    if (logTimeoutRef.current) {
      clearTimeout(logTimeoutRef.current);
      logTimeoutRef.current = null;
    }
    setAnchorElLog(event.currentTarget);
  };

  const handleLogMouseLeave = () => {
    logTimeoutRef.current = setTimeout(() => {
      setAnchorElLog(null);
    }, 150);
  };

  const handleLogMenuEnter = () => {
    if (logTimeoutRef.current) {
      clearTimeout(logTimeoutRef.current);
      logTimeoutRef.current = null;
    }
  };

  const handleAdminClick = (event) => {
    setAnchorElAdmin(event.currentTarget);
  };

  const handleAdminClose = () => {
    setAnchorElAdmin(null);
  };

  const handleAdminMouseEnter = (event) => {
    if (adminTimeoutRef.current) {
      clearTimeout(adminTimeoutRef.current);
      adminTimeoutRef.current = null;
    }
    setAnchorElAdmin(event.currentTarget);
  };

  const handleAdminMouseLeave = () => {
    adminTimeoutRef.current = setTimeout(() => {
      setAnchorElAdmin(null);
    }, 150);
  };

  const handleAdminMenuEnter = () => {
    if (adminTimeoutRef.current) {
      clearTimeout(adminTimeoutRef.current);
      adminTimeoutRef.current = null;
    }
  };

  const handleAdminToggleMobile = () => {
    setAdminOpenMobile(!adminOpenMobile);
  };

  const handlePWAInstallClick = async () => {
    const outcome = await handleInstall();
    if (outcome === 'ios') {
      setShowIOSDialog(true);
    }
  };

  const handleCloseIOSDialog = () => {
    setShowIOSDialog(false);
  };

  // Filter pages based on authentication and roles
  const userRoles = keycloak?.realmAccess?.roles || [];
  const visiblePages = pages.filter(page => {
    if (page.public) return true;
    if (page.unauthOnly && isAuthenticated) return false;
    if (page.unauthOnly && !isAuthenticated) return true;
    if (page.protected && !isAuthenticated) return false;
    if (page.role && !userRoles.includes(page.role)) return false;
    // For admin menu, check if user has at least one of the submenu roles
    if (page.adminMenu && page.submenu) {
      const hasAnyAdminRole = page.submenu.some(sub => userRoles.includes(sub.role));
      if (!hasAnyAdminRole) return false;
    }
    return true;
  }).map(page => {
    // Filter admin submenu items based on roles
    if (page.adminMenu && page.submenu) {
      return {
        ...page,
        submenu: page.submenu.filter(sub => userRoles.includes(sub.role))
      };
    }
    return page;
  });

  const drawer = (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ my: 1.5, display: 'flex', justifyContent: 'center' }}>
        <img
          src="/images/logo-full.svg"
          alt="GlucoScout Logo"
          style={{ height: '50px', cursor: 'pointer' }}
          onClick={() => handleNavigation('/home')}
        />
      </Box>
      <List>
        {visiblePages.map((page) => {
          // Keep Admin submenu as collapsible
          if (page.submenu && page.name === t('nav.admin')) {
            return (
              <Box key={page.name}>
                <ListItem disablePadding>
                  <ListItemButton onClick={handleAdminToggleMobile}>
                    <Box sx={{ mr: 2, display: 'flex' }}>{page.icon}</Box>
                    <ListItemText primary={page.name} />
                    {adminOpenMobile ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={adminOpenMobile} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {page.submenu.map((subItem) => (
                      <ListItemButton
                        key={subItem.name}
                        sx={{ pl: 4 }}
                        selected={location.pathname === subItem.path}
                        onClick={() => handleNavigation(subItem.path)}
                      >
                        <ListItemIcon>{subItem.icon}</ListItemIcon>
                        <ListItemText primary={subItem.name} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          }

          // Flatten Calculator and Log submenus
          if (page.submenu && (page.name === t('nav.calculators') || page.name === t('nav.log'))) {
            return page.submenu.map((subItem) => (
              <ListItem key={subItem.name} disablePadding>
                <ListItemButton
                  selected={location.pathname === subItem.path}
                  onClick={() => handleNavigation(subItem.path)}
                >
                  <Box sx={{ mr: 2, display: 'flex' }}>{subItem.icon}</Box>
                  <ListItemText primary={subItem.name} />
                </ListItemButton>
              </ListItem>
            ));
          }

          // Regular items render as before
          return (
            <ListItem key={page.name} disablePadding>
              <ListItemButton
                selected={location.pathname === page.path}
                onClick={() => handleNavigation(page.path)}
              >
                <Box sx={{ mr: 2, display: 'flex' }}>{page.icon}</Box>
                <ListItemText primary={page.name} />
              </ListItemButton>
            </ListItem>
          );
        })}
        {isAuthenticated && !isStandalone && canInstall && (
          <ListItem disablePadding>
            <ListItemButton onClick={handlePWAInstallClick}>
              <Box sx={{ mr: 2, display: 'flex' }}>
                <GetAppIcon />
              </Box>
              <ListItemText primary={t('pwa.installApp')} />
            </ListItemButton>
          </ListItem>
        )}
        {isAuthenticated && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/settings')}>
              <Box sx={{ mr: 2, display: 'flex' }}>
                <SettingsIcon />
              </Box>
              <ListItemText primary={t('nav.settings')} />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky" sx={{
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        background: '#212529',
        boxShadow: 'none',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Box
              onClick={() => navigate('/home')}
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <img
                src="/images/logo-full.svg"
                alt="GlucoScout Logo"
                style={{ height: '48px' }}
              />
            </Box>

            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="menu"
                onClick={handleDrawerToggle}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            </Box>

            <Box
              onClick={() => navigate('/home')}
              sx={{
                display: { xs: 'flex', md: 'none' },
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                alignItems: 'center',
                cursor: 'pointer',
                pointerEvents: 'all',
              }}
            >
              <img
                src="/images/logo-navbar-mobile.svg"
                alt="GlucoScout Logo"
                style={{ height: '36px' }}
              />
            </Box>

            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 1, ml: 3 }}>
              {visiblePages.map((page) => (
                page.submenu ? (
                  <Box
                    key={page.name}
                    onMouseEnter={
                      page.name === t('nav.calculators') ? handleCalculatorsMouseEnter :
                      page.name === t('nav.log') ? handleLogMouseEnter :
                      handleAdminMouseEnter
                    }
                    onMouseLeave={
                      page.name === t('nav.calculators') ? handleCalculatorsMouseLeave :
                      page.name === t('nav.log') ? handleLogMouseLeave :
                      handleAdminMouseLeave
                    }
                  >
                    <Button
                      onClick={
                        page.name === t('nav.calculators') ? handleCalculatorsClick :
                        page.name === t('nav.log') ? handleLogClick :
                        handleAdminClick
                      }
                      sx={{
                        color: (page.name === t('nav.calculators') && location.pathname.startsWith('/calc')) ||
                          (page.name === t('nav.log') && (location.pathname.startsWith('/log') || location.pathname === '/sensors')) ||
                          (page.name === t('nav.admin') && location.pathname.startsWith('/admin'))
                            ? '#90caf9' : '#ADB5BD',
                        fontWeight: 500,
                        textTransform: 'none',
                        px: 2,
                        py: 1,
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        background: 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(144, 202, 249, 0.08)',
                          color: '#90caf9',
                        },
                        '& .MuiButton-startIcon': {
                          marginRight: '6px',
                          color: (page.name === t('nav.calculators') && location.pathname.startsWith('/calc')) ||
                            (page.name === t('nav.log') && (location.pathname.startsWith('/log') || location.pathname === '/sensors')) ||
                            (page.name === t('nav.admin') && location.pathname.startsWith('/admin'))
                              ? '#90caf9' : '#ADB5BD',
                          transition: 'color 0.2s ease',
                        },
                        '&:hover .MuiButton-startIcon': {
                          color: '#90caf9',
                        },
                        '& .MuiButton-endIcon': {
                          marginLeft: '4px',
                          transition: 'transform 0.2s ease',
                          color: '#ADB5BD',
                        },
                        '&:hover .MuiButton-endIcon': {
                          transform: 'rotate(180deg)',
                          color: '#90caf9',
                        },
                      }}
                      startIcon={page.icon}
                      endIcon={<ExpandMoreIcon />}
                    >
                      {page.name}
                    </Button>
                    <Menu
                      anchorEl={
                        page.name === t('nav.calculators') ? anchorElCalculators :
                        page.name === t('nav.log') ? anchorElLog :
                        anchorElAdmin
                      }
                      open={
                        page.name === t('nav.calculators') ? Boolean(anchorElCalculators) :
                        page.name === t('nav.log') ? Boolean(anchorElLog) :
                        Boolean(anchorElAdmin)
                      }
                      onClose={
                        page.name === t('nav.calculators') ? handleCalculatorsClose :
                        page.name === t('nav.log') ? handleLogClose :
                        handleAdminClose
                      }
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      MenuListProps={{
                        onMouseEnter: page.name === t('nav.calculators') ? handleCalculatorsMenuEnter :
                          page.name === t('nav.log') ? handleLogMenuEnter :
                          handleAdminMenuEnter,
                        onMouseLeave: page.name === t('nav.calculators') ? handleCalculatorsMouseLeave :
                          page.name === t('nav.log') ? handleLogMouseLeave :
                          handleAdminMouseLeave,
                      }}
                      sx={{
                        mt: 1,
                        pointerEvents: 'none',
                        '& .MuiPaper-root': {
                          pointerEvents: 'auto',
                          background: 'linear-gradient(135deg, rgba(44, 48, 52, 0.98) 0%, rgba(33, 37, 41, 1) 100%)',
                          backdropFilter: 'blur(12px)',
                          border: '1px solid rgba(144, 202, 249, 0.15)',
                          borderRadius: 2,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                          minWidth: 200,
                        },
                      }}
                    >
                      {page.submenu.map((subItem) => (
                        <MenuItem
                          key={subItem.name}
                          onClick={() => {
                            handleNavigation(subItem.path);
                            if (page.name === t('nav.calculators')) handleCalculatorsClose();
                            else if (page.name === t('nav.log')) handleLogClose();
                            else handleAdminClose();
                          }}
                          selected={location.pathname === subItem.path}
                          sx={{
                            py: 1.5,
                            px: 2,
                            color: '#DEE2E6',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: 'rgba(144, 202, 249, 0.08)',
                              color: '#90caf9',
                              '& .MuiListItemIcon-root': {
                                color: '#90caf9',
                              },
                            },
                            '&.Mui-selected': {
                              background: 'rgba(144, 202, 249, 0.12)',
                              color: '#90caf9',
                              '& .MuiListItemIcon-root': {
                                color: '#90caf9',
                              },
                              '&:hover': {
                                background: 'rgba(144, 202, 249, 0.15)',
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ color: '#ADB5BD', minWidth: 36 }}>
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText
                            primaryTypographyProps={{
                              fontSize: '0.9rem',
                              fontWeight: 500,
                            }}
                          >
                            {subItem.name}
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                ) : (
                  <Button
                    key={page.name}
                    onClick={() => handleNavigation(page.path)}
                    sx={{
                      color: location.pathname === page.path ? '#90caf9' : '#ADB5BD',
                      fontWeight: 500,
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                      borderRadius: '20px',
                      fontSize: '0.9rem',
                      background: 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(144, 202, 249, 0.08)',
                        color: '#90caf9',
                      },
                      '& .MuiButton-startIcon': {
                        marginRight: '6px',
                        color: location.pathname === page.path ? '#90caf9' : '#ADB5BD',
                        transition: 'color 0.2s ease',
                      },
                      '&:hover .MuiButton-startIcon': {
                        color: '#90caf9',
                      },
                    }}
                    startIcon={page.icon}
                  >
                    {page.name}
                  </Button>
                )
              ))}
            </Box>

            {!initialized ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : isAuthenticated ? (
              <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ mr: -2 }}>
                  <NewsIcon />
                </Box>
                <Tooltip title={t('nav.help')}>
                  <IconButton
                    onClick={() => navigate('/help')}
                    sx={{ color: '#ADB5BD', '&:hover': { color: '#90caf9' } }}
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('nav.settings')}>
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{
                    mt: '45px',
                    '& .MuiPaper-root': {
                      background: 'linear-gradient(135deg, rgba(44, 48, 52, 0.98) 0%, rgba(33, 37, 41, 1) 100%)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(144, 202, 249, 0.15)',
                      borderRadius: 2,
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                      minWidth: 180,
                    },
                  }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem
                    onClick={() => handleUserMenuClick('settings')}
                    sx={{
                      py: 1.5,
                      px: 2,
                      color: '#DEE2E6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(144, 202, 249, 0.08)',
                        color: '#90caf9',
                        '& .MuiSvgIcon-root': {
                          color: '#90caf9',
                        },
                      },
                    }}
                  >
                    <SettingsIcon sx={{ mr: 1.5, fontSize: 20, color: '#ADB5BD' }} />
                    <Typography fontSize="0.9rem" fontWeight={500}>{t('nav.settings')}</Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleUserMenuClick('logout')}
                    sx={{
                      py: 1.5,
                      px: 2,
                      color: '#DEE2E6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(244, 67, 54, 0.08)',
                        color: '#f44336',
                        '& .MuiSvgIcon-root': {
                          color: '#f44336',
                        },
                      },
                    }}
                  >
                    <LogoutIcon sx={{ mr: 1.5, fontSize: 20, color: '#ADB5BD' }} />
                    <Typography fontSize="0.9rem" fontWeight={500}>{t('nav.logout')}</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  startIcon={<LoginIcon />}
                  onClick={() => keycloak.login()}
                  sx={{
                    color: '#212529',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 2.5,
                    py: 0.8,
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    background: 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #64b5f6 0%, #42a5f5 100%)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 20px rgba(144, 202, 249, 0.4)',
                    },
                    '& .MuiButton-startIcon': {
                      color: '#212529',
                    },
                  }}
                >
                  {t('nav.signIn')}
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <SwipeableDrawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        onOpen={handleDrawerToggle}
        disableBackdropTransition
        disableDiscovery={false}
        swipeAreaWidth={30}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </SwipeableDrawer>

      <Container
        component="main"
        maxWidth={['/admin/users', '/admin/food'].includes(location.pathname) ? false : 'xl'}
        sx={{ mt: 0, mb: 0, flexGrow: 1, px: ['/admin/users', '/admin/food'].includes(location.pathname) ? { xs: 2, md: 0 } : undefined }}
      >
        {children}
      </Container>

      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="body2" color="text.secondary" align="center">
            GlucoScout © {new Date().getFullYear()}
          </Typography>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('footer.poweredBy')}
            </Typography>
            <a 
              href="https://www.timeforit.se" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'inline-block' }}
            >
              <img 
                src="/images/time-for-it-logo.png" 
                alt="Time for IT" 
                style={{ height: '40px', opacity: 0.8 }}
              />
            </a>
          </Box>
        </Container>
      </Box>

      {/* iOS Install Instructions Dialog */}
      <Dialog
        open={showIOSDialog}
        onClose={handleCloseIOSDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('pwa.iosInstallTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {t('pwa.iosInstallDescription')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  borderRadius: 1,
                  p: 1,
                  display: 'flex',
                }}
              >
                <IosShareIcon sx={{ color: 'white' }} />
              </Box>
              <Typography variant="body2">
                {t('pwa.iosStep1')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  borderRadius: 1,
                  p: 1,
                  display: 'flex',
                }}
              >
                <AddBoxOutlinedIcon sx={{ color: 'white' }} />
              </Box>
              <Typography variant="body2">
                {t('pwa.iosStep2')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseIOSDialog}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Layout;
